/**
 * TanStack Query hooks for the coach: the home briefing, protected areas and the coach's memory.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  ProtectedAreaOut,
  HomeBriefing,
  CoachMemoryList,
  CoachMemoryOut,
  MemoryCreate,
  MemoryUpdate,
  ConsolidationRunOut,
} from '../types'
import { noneOn404, useCrudMutations } from './shared'

/**
 * Fetch the coach home-screen briefing (week status, what's due, alerts, recap).
 * Query key: ['coach', 'home-briefing']
 */
export function useHomeBriefing() {
  return useQuery({
    queryKey: ['coach', 'home-briefing'],
    queryFn: () => apiFetch<HomeBriefing>('/api/coach/home-briefing'),
  })
}

// ── Protected areas (#277, PC-I66, PC-I69) ─────────────────────────

/**
 * The areas the coach keeps out of the user's sessions after an injured
 * answer, until they clear them. Query key: ['protections'].
 *
 * Refetched whenever the Coach tab mounts, unlike the app's Infinity default:
 * an injured answer is given on the phone, mid-session, and no sync watcher
 * hears about it, so a list read an hour ago could hide an area the user has
 * come here to clear.
 */
export function useProtections() {
  return useQuery({
    queryKey: ['protections'],
    queryFn: () => apiFetch<ProtectedAreaOut[]>('/api/coach/protections'),
    refetchOnMount: 'always',
  })
}

/**
 * Clear one protected area (PC-I69): the user says it is clear, a week or a
 * month on. A 404 (already cleared elsewhere) is the answer the user wanted,
 * so it refreshes the list rather than failing. Invalidates ['protections'].
 */
export function useClearProtection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (area: string) => {
      await noneOn404(apiFetch<void>(`/api/coach/protections/${area}/clear`, { method: 'POST' }))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['protections'] }),
  })
}

// ── Coach Memory ─────────────────────────────────────────────────────

const MEMORY_KEY = ['coach', 'memories'] as const

/** Everything the coach durably knows — standing facts + dated session notes. */
export function useCoachMemories() {
  return useQuery({
    queryKey: MEMORY_KEY,
    queryFn: () => apiFetch<CoachMemoryList>('/api/coach/memories'),
  })
}

/** Recent consolidation runs — the transparency/audit trail. */
export function useConsolidationRuns() {
  return useQuery({
    queryKey: ['coach', 'memories', 'consolidations'],
    queryFn: () =>
      apiFetch<ConsolidationRunOut[]>('/api/coach/memories/consolidations'),
  })
}

function useMemoryMutations() {
  return useCrudMutations<CoachMemoryOut, MemoryCreate, MemoryUpdate>(
    '/api/coach/memories',
    MEMORY_KEY,
    'PATCH',
  )
}

export function useCreateMemory() {
  return useMemoryMutations().create
}

export function useUpdateMemory() {
  return useMemoryMutations().update
}

export function useDeleteMemory() {
  return useMemoryMutations().remove
}
